import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ArrowLeft, ArrowUp, Bell, Play, Radio, RefreshCw, Tv, X, } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, } from "react";
import { isInProgressGameScore, parseGameScore, } from "../domain/game-state";
import { isNearFeedEnd } from "../domain/feed-pagination";
import { gameModeScrollTarget } from "../domain/game-mode-scroll";
import { primaryViewingProvider, } from "../domain/demo-viewer";
import { resolveArticleDestination } from "../domain/articles";
import { ArticleReader } from "./ArticleReader";
import { TicketOfferView, UnitCard } from "./UnitCard";
import { VerticalClipPlayer } from "./VerticalClipPlayer";
const storyGameModes = ["feed", "box", "stats", "watch"];
const liveArrivalDelayMs = 6_000;
const liveArrivalResolveMs = 650;
const pullRefreshThreshold = 52;
const tabSwipeThreshold = 44;
const useBrowserLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;
const acknowledgePrototypeHostIntent = (_destination) => { };
export function gameAlertChoices(isFinal) {
    return isFinal
        ? [
            { id: "game-recap", label: "Game recap available" },
            {
                id: "condensed-game",
                label: "Condensed game available",
            },
        ]
        : [
            { id: "game-end", label: "Game end" },
            { id: "game-recap", label: "Game recap available" },
            {
                id: "condensed-game",
                label: "Condensed game available",
            },
        ];
}
export function adjacentStoryGameMode(mode, direction, hasStats) {
    const step = direction === "left" ? 1 : -1;
    let index = storyGameModes.indexOf(mode) + step;
    while (index >= 0 && index < storyGameModes.length) {
        const candidate = storyGameModes[index];
        if (hasStats ||
            (candidate !== "box" && candidate !== "stats")) {
            return candidate;
        }
        index += step;
    }
    return mode;
}
export function insertFeedItemInCanonicalOrder(visibleItems, canonicalItems, insertedItem) {
    if (visibleItems.some((item) => item.display.id === insertedItem.display.id)) {
        return visibleItems;
    }
    const canonicalIndex = canonicalItems.findIndex((item) => item.display.id === insertedItem.display.id);
    if (canonicalIndex < 0) {
        return visibleItems;
    }
    const visibleIds = new Set(visibleItems.map((item) => item.display.id));
    const insertionIndex = canonicalItems
        .slice(0, canonicalIndex)
        .filter((item) => visibleIds.has(item.display.id)).length;
    return [
        ...visibleItems.slice(0, insertionIndex),
        insertedItem,
        ...visibleItems.slice(insertionIndex),
    ];
}
export function insertFeedItemsInCanonicalOrder(visibleItems, canonicalItems, insertedItems) {
    return insertedItems.reduce((items, insertedItem) => insertFeedItemInCanonicalOrder(items, canonicalItems, insertedItem), visibleItems);
}
function TeamMark({ code, label, }) {
    return (_jsxs("span", { "aria-label": label, className: "story-game__team-mark", children: [_jsx("span", { "aria-hidden": "true", children: code }), _jsx("small", { children: code })] }));
}
function GameStoryMark({ awayCode, homeCode, hasNewPages, onOpen, story, }) {
    return (_jsx("button", { "aria-label": `Open ${story.label} game Story. ${hasNewPages
            ? "New pages available."
            : "All pages viewed."}`, "aria-haspopup": "dialog", className: `story-game__story-button ${hasNewPages
            ? "story-game__story-button--updated"
            : "story-game__story-button--seen"}`, "data-story-id": story.destination?.id, onClick: onOpen, type: "button", children: _jsxs("span", { "aria-hidden": "true", children: [_jsx("span", { children: awayCode }), _jsx("span", { children: homeCode })] }) }));
}
function GameStoryPlayer({ companion, item, onClose, story, }) {
    return (_jsxs("section", { "aria-label": `${story.label} Story`, "aria-modal": "true", className: "game-story-player", "data-story-id": story.destination?.id, role: "dialog", children: [_jsx("div", { "aria-hidden": "true", className: "game-story-player__media" }), _jsxs("div", { "aria-hidden": "true", className: "game-story-player__progress", children: [_jsx("span", {}), _jsx("span", {}), _jsx("span", {}), _jsx("span", {}), _jsx("span", {})] }), _jsxs("header", { className: "game-story-player__header", children: [_jsxs("span", { "aria-hidden": "true", className: "game-story-player__mark", children: [_jsx("span", { children: story.thumbnail?.primary }), _jsx("span", { children: story.thumbnail?.secondary })] }), _jsxs("span", { children: [_jsx("strong", { children: story.label }), _jsx("small", { children: story.status })] }), _jsx("button", { "aria-label": "Close game Story", onClick: onClose, type: "button", children: _jsx(X, { "aria-hidden": "true" }) })] }), _jsxs("div", { className: "game-story-player__copy", children: [_jsx("small", { children: companion.matchup }), item?.display.headline ? (_jsx("h2", { children: item.display.headline })) : null, item?.display.dek ? _jsx("p", { children: item.display.dek }) : null] })] }));
}
export function GameAlertsSheet({ choices, onClose, onToggle, selectedIds, }) {
    return (_jsx("section", { "aria-label": "Game alerts", "aria-modal": "true", className: "game-alerts-sheet", role: "dialog", children: _jsxs("div", { className: "game-alerts-sheet__panel", children: [_jsxs("header", { children: [_jsxs("div", { children: [_jsx("small", { children: "GAME ALERTS" }), _jsx("h2", { children: "Choose your alerts" })] }), _jsx("button", { "aria-label": "Close game alerts", autoFocus: true, onClick: onClose, type: "button", children: _jsx(X, { "aria-hidden": "true" }) })] }), _jsx("div", { className: "game-alerts-sheet__choices", children: choices.map((choice) => {
                        const isSelected = selectedIds.has(choice.id);
                        return (_jsxs("button", { "aria-checked": isSelected, onClick: () => onToggle(choice.id), role: "switch", type: "button", children: [_jsx("span", { children: choice.label }), _jsx("span", { "aria-hidden": "true", className: "game-alerts-sheet__switch", children: _jsx("span", {}) })] }, choice.id));
                    }) }), _jsx("button", { className: "game-alerts-sheet__done", onClick: onClose, type: "button", children: "Done" })] }) }));
}
function PeriodTable({ stats }) {
    const awayTotal = stats.periods.reduce((total, period) => total + period.away, 0);
    const homeTotal = stats.periods.reduce((total, period) => total + period.home, 0);
    return (_jsxs("section", { "aria-labelledby": "period-score-heading", className: "story-stats__card", children: [_jsxs("header", { children: [_jsx("p", { children: "Score by period" }), _jsx("strong", { id: "period-score-heading", children: stats.stateLabel })] }), _jsxs("div", { className: "story-stats__period-grid", style: {
                    gridTemplateColumns: `42px repeat(${stats.periods.length + 1}, minmax(0, 1fr))`,
                }, children: [_jsx("span", {}), stats.periods.map((period) => (_jsx("small", { children: period.label }, period.label))), _jsx("small", { children: "T" }), _jsx("strong", { children: stats.awayCode }), stats.periods.map((period) => (_jsx("span", { children: period.away }, `${period.label}-away`))), _jsx("b", { children: awayTotal }), _jsx("strong", { children: stats.homeCode }), stats.periods.map((period) => (_jsx("span", { children: period.home }, `${period.label}-home`))), _jsx("b", { children: homeTotal })] })] }));
}
function PlayerRows({ code, players, }) {
    return (_jsxs("section", { className: "story-stats__players", children: [_jsxs("header", { children: [_jsx("strong", { children: code }), _jsx("span", { children: "PTS" }), _jsx("span", { children: "REB" }), _jsx("span", { children: "AST" })] }), players.map((player) => (_jsxs("article", { children: [_jsxs("span", { children: [_jsx("strong", { children: player.name }), _jsxs("small", { children: ["FG ", player.fieldGoals, " \u00B7 3PT ", player.threePointers, " \u00B7 FT ", player.freeThrows] })] }), _jsx("b", { children: player.points }), _jsx("b", { children: player.rebounds }), _jsx("b", { children: player.assists })] }, player.name)))] }));
}
function StatsPanel({ stats }) {
    return (_jsxs("div", { className: "story-stats", children: [_jsx(PeriodTable, { stats: stats }), _jsxs("section", { className: "story-stats__card story-stats__shape", children: [_jsx("p", { children: "Shape of the game" }), stats.gameShape.map((item) => (_jsxs("div", { children: [_jsx("span", { children: item.label }), _jsx("strong", { children: item.value })] }, item.label)))] }), _jsxs("section", { className: "story-stats__card", children: [_jsxs("header", { children: [_jsx("strong", { children: stats.awayCode }), _jsx("p", { children: "Team comparison" }), _jsx("strong", { children: stats.homeCode })] }), _jsx("div", { className: "story-stats__comparison", children: stats.comparison.map((item) => (_jsxs("div", { children: [_jsx("strong", { children: item.away }), _jsx("span", { children: item.label }), _jsx("strong", { children: item.home })] }, item.label))) })] }), _jsxs("section", { className: "story-stats__card", children: [_jsxs("header", { children: [_jsx("strong", { children: stats.awayCode }), _jsx("p", { children: "Shot profile" }), _jsx("strong", { children: stats.homeCode })] }), _jsx("div", { className: "story-stats__shots", children: stats.shotProfile.map((zone) => (_jsxs("article", { children: [_jsxs("strong", { children: [zone.awayAttempts, " \u00B7 ", zone.awayPercentage] }), _jsx("span", { children: zone.label }), _jsxs("strong", { children: [zone.homeAttempts, " \u00B7 ", zone.homePercentage] })] }, zone.label))) })] }), _jsxs("section", { className: "story-stats__leaders", children: [_jsx("p", { children: "Leaders" }), _jsxs("div", { children: [_jsxs("article", { children: [_jsx("small", { children: stats.awayCode }), _jsx("strong", { children: stats.awayPlayers[0]?.name }), _jsxs("span", { children: [stats.awayPlayers[0]?.points, " PTS \u00B7", " ", stats.awayPlayers[0]?.rebounds, " REB \u00B7", " ", stats.awayPlayers[0]?.assists, " AST"] })] }), _jsxs("article", { children: [_jsx("small", { children: stats.homeCode }), _jsx("strong", { children: stats.homePlayers[0]?.name }), _jsxs("span", { children: [stats.homePlayers[0]?.points, " PTS \u00B7", " ", stats.homePlayers[0]?.rebounds, " REB \u00B7", " ", stats.homePlayers[0]?.assists, " AST"] })] })] })] })] }));
}
function BoxScorePanel({ stats }) {
    const [teamView, setTeamView] = useState("away");
    return (_jsxs("div", { className: "story-box", children: [_jsxs("nav", { "aria-label": "Box score teams", className: "story-box__tabs", children: [_jsx("button", { "aria-pressed": teamView === "away", onClick: () => setTeamView("away"), type: "button", children: stats.awayCode }), _jsx("button", { "aria-pressed": teamView === "home", onClick: () => setTeamView("home"), type: "button", children: stats.homeCode })] }), _jsxs("p", { className: "story-box__state", children: ["Box score \u00B7 ", stats.stateLabel] }), teamView === "away" ? (_jsx(PlayerRows, { code: stats.awayCode, players: stats.awayPlayers })) : (_jsx(PlayerRows, { code: stats.homeCode, players: stats.homePlayers }))] }));
}
function WatchPanel({ broadcastActionLabel, companion, isFinal, viewingAccess, }) {
    const isBlackout = viewingAccess === "blackout";
    const hasLeaguePassPlayer = viewingAccess === "league-pass";
    const hasLeaguePassAccess = viewingAccess === "league-pass" ||
        viewingAccess === "league-pass-signed-out" ||
        viewingAccess === "league-pass-unentitled";
    const isLoggedOut = viewingAccess === "league-pass-signed-out";
    const needsLeaguePass = viewingAccess === "league-pass-unentitled";
    const broadcaster = isBlackout
        ? null
        : hasLeaguePassAccess
            ? "League Pass"
            : primaryViewingProvider(companion.game);
    const availability = isBlackout
        ? []
        : hasLeaguePassAccess
            ? ["League Pass"]
            : broadcaster
                ? [broadcaster]
                : [];
    return (_jsxs("div", { className: "story-watch", children: [!hasLeaguePassPlayer ? (_jsxs("div", { className: "story-watch__banner", children: [_jsx("span", { children: companion.title }), _jsx("strong", { children: isBlackout
                            ? "Not available in your area"
                            : isLoggedOut
                                ? "Sign in to watch on League Pass"
                                : needsLeaguePass
                                    ? "Get League Pass"
                                    : isFinal
                                        ? "Highlights and recap"
                                        : broadcaster
                                            ? `Tonight on ${broadcaster}`
                                            : "Live viewing options" })] })) : null, _jsx("p", { children: hasLeaguePassPlayer
                    ? isFinal
                        ? "League Pass replay"
                        : "Now playing"
                    : isBlackout
                        ? "Viewing restriction"
                        : hasLeaguePassAccess
                            ? "League Pass"
                            : isFinal
                                ? "Game video"
                                : "Where to watch" }), _jsx("h2", { children: hasLeaguePassPlayer
                    ? isFinal
                        ? "Watch from the start or jump to the recap"
                        : "Keep watching while you follow the game"
                    : isBlackout
                        ? "This live game is unavailable in this location"
                        : isLoggedOut
                            ? "Sign in to start watching"
                            : needsLeaguePass
                                ? "Subscribe to watch this game"
                                : isFinal
                                    ? `${companion.title}: highlights and recap`
                                    : broadcaster
                                        ? `${companion.title} is available on ${broadcaster}`
                                        : `Where to watch ${companion.title}` }), hasLeaguePassPlayer ? (_jsxs("div", { className: "story-watch__controls", children: [_jsxs("button", { type: "button", children: [_jsx(Play, { "aria-hidden": "true" }), isFinal ? "Watch from start" : "Jump to live"] }), _jsx("button", { type: "button", children: "Playback options" })] })) : (_jsxs("button", { type: "button", children: [_jsx(Tv, { "aria-hidden": "true" }), isBlackout
                        ? "See availability"
                        : isLoggedOut
                            ? "Sign in to watch"
                            : needsLeaguePass
                                ? "See League Pass options"
                                : isFinal
                                    ? "Watch highlights"
                                    : (broadcastActionLabel ??
                                        (broadcaster
                                            ? `Watch on ${broadcaster}`
                                            : "Tap To Watch"))] })), availability.length > 0 ? (_jsxs("section", { children: [_jsx("strong", { children: isFinal ? "Coverage" : "Where to watch" }), availability.map((channel, index) => (_jsxs("div", { children: [_jsx("span", { children: hasLeaguePassAccess
                                    ? "NBA app"
                                    : isFinal
                                        ? index === 0
                                            ? "Primary broadcaster"
                                            : "Also broadcast by"
                                        : index === 0
                                            ? "National"
                                            : "Also available" }), _jsx("b", { children: channel })] }, channel)))] })) : null, companion.ticketOffer ? (_jsx(TicketOfferView, { accessibleName: companion.ticketOffer.interaction.accessibleName ??
                    undefined, display: companion.ticketOffer.display, onActivate: () => acknowledgePrototypeHostIntent(companion.ticketOffer.interaction.destination) })) : null, _jsx("small", { children: "Availability depends on location and subscription." })] }));
}
export function StoryFeedGameScreenPhone({ activeMode, articles = [], broadcastActionLabel, companion, deviceTime, embedded = false, initialMode = "feed", liveArrivalItemIds, onBack, onModeChange, stats, viewingAccess = "broadcast-only", }) {
    const [internalMode, setInternalMode] = useState(initialMode);
    const mode = activeMode ?? internalMode;
    const [openClip, setOpenClip] = useState(null);
    const [openArticleId, setOpenArticleId] = useState(null);
    const [isStoryOpen, setIsStoryOpen] = useState(false);
    const [isAlertsOpen, setIsAlertsOpen] = useState(false);
    const [selectedAlertIds, setSelectedAlertIds] = useState(() => new Set());
    const [hasNewStoryPages, setHasNewStoryPages] = useState(companion.story?.state !== "seen");
    const alertButtonRef = useRef(null);
    const playerRef = useRef(null);
    const viewportRef = useRef(null);
    const clipLauncherRef = useRef(null);
    const articleLauncherRef = useRef(null);
    const isLoadingOlderRef = useRef(false);
    const feedScrollTopRef = useRef(0);
    const previousModeRef = useRef(mode);
    const liveArrivalTimerRef = useRef(null);
    const pointerStartRef = useRef(null);
    const [liveArrivalState, setLiveArrivalState] = useState("waiting");
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [pullDistance, setPullDistance] = useState(0);
    const [swipeOffset, setSwipeOffset] = useState(0);
    const [tabTransitionDirection, setTabTransitionDirection] = useState(null);
    const [visibleFeedItemCount, setVisibleFeedItemCount] = useState(12);
    const parsed = useMemo(() => parseGameScore(companion.score), [companion.score]);
    const { game } = companion;
    const isFinal = parsed?.state.startsWith("FINAL") ?? false;
    const alertChoices = gameAlertChoices(isFinal);
    const selectedAlertCount = selectedAlertIds.size;
    const isOverlayOpen = Boolean(openClip) ||
        Boolean(openArticleId) ||
        isStoryOpen ||
        isAlertsOpen;
    const awayCode = parsed?.awayCode ?? game.away?.code ?? "AWY";
    const homeCode = parsed?.homeCode ?? game.home?.code ?? "HME";
    const primaryBroadcaster = viewingAccess === "broadcast-only"
        ? primaryViewingProvider(game)
        : "League Pass";
    const watchLabel = viewingAccess === "blackout"
        ? "Unavailable live"
        : viewingAccess === "league-pass-signed-out"
            ? "Sign in to watch"
            : viewingAccess === "league-pass-unentitled"
                ? "Get League Pass"
                : isFinal && viewingAccess === "broadcast-only"
                    ? "Highlights and recap"
                    : isFinal
                        ? `Watch replay on ${primaryBroadcaster}`
                        : viewingAccess === "broadcast-only"
                            ? (broadcastActionLabel ??
                                (primaryBroadcaster
                                    ? `Watch on ${primaryBroadcaster}`
                                    : "Tap To Watch"))
                            : `Watch on ${primaryBroadcaster}`;
    const liveArrivalItemIdsKey = liveArrivalItemIds?.join("|") ?? "";
    const liveArrivalItems = useMemo(() => {
        const arrivalIds = new Set(liveArrivalItemIdsKey.split("|").filter(Boolean));
        return companion.items.filter((item) => arrivalIds.has(item.display.id));
    }, [companion.items, liveArrivalItemIdsKey]);
    const settledFeedItems = useMemo(() => {
        const arrivalIds = new Set(liveArrivalItems.map((item) => item.display.id));
        return companion.items.filter((item) => !arrivalIds.has(item.display.id));
    }, [companion.items, liveArrivalItems]);
    const visibleFeedItems = settledFeedItems.slice(0, visibleFeedItemCount);
    const displayedFeedItems = liveArrivalState === "inserted" && liveArrivalItems.length > 0
        ? insertFeedItemsInCanonicalOrder(visibleFeedItems, companion.items, liveArrivalItems)
        : visibleFeedItems;
    const hasOlderFeedItems = visibleFeedItemCount < settledFeedItems.length;
    const firstFeedItemId = companion.items[0]?.display.id ?? "";
    const lastFeedItemId = companion.items[companion.items.length - 1]?.display.id ?? "";
    const storyItem = companion.story?.destination
        ? companion.items.find((item) => item.interaction.destination.type ===
            companion.story?.destination?.type &&
            "id" in item.interaction.destination &&
            item.interaction.destination.id ===
                companion.story?.destination?.id)
        : undefined;
    const scrollContextKey = [
        companion.gameId,
        companion.score,
        companion.items.length,
        firstFeedItemId,
        lastFeedItemId,
        viewingAccess,
    ].join("|");
    const scrollContextKeyRef = useRef(scrollContextKey);
    const feedTop = useCallback(() => viewingAccess === "league-pass"
        ? (playerRef.current?.offsetHeight ?? 0)
        : 0, [viewingAccess]);
    const scrollToFeedTop = useCallback(() => {
        viewportRef.current?.scrollTo({
            behavior: "smooth",
            top: feedTop(),
        });
    }, [feedTop]);
    const clearLiveArrivalTimer = useCallback(() => {
        if (liveArrivalTimerRef.current !== null) {
            window.clearTimeout(liveArrivalTimerRef.current);
            liveArrivalTimerRef.current = null;
        }
    }, []);
    const finishRefreshWithoutArrival = useCallback(() => {
        clearLiveArrivalTimer();
        setIsRefreshing(true);
        liveArrivalTimerRef.current = window.setTimeout(() => {
            setIsRefreshing(false);
            liveArrivalTimerRef.current = null;
        }, 500);
    }, [clearLiveArrivalTimer]);
    const insertLiveArrival = useCallback(() => {
        if (liveArrivalItems.length === 0) {
            finishRefreshWithoutArrival();
            return;
        }
        if (liveArrivalState === "inserted") {
            finishRefreshWithoutArrival();
            return;
        }
        if (liveArrivalState === "loading") {
            return;
        }
        clearLiveArrivalTimer();
        setPullDistance(0);
        setIsRefreshing(true);
        setLiveArrivalState("loading");
        liveArrivalTimerRef.current = window.setTimeout(() => {
            setLiveArrivalState("inserted");
            setIsRefreshing(false);
            liveArrivalTimerRef.current = null;
        }, liveArrivalResolveMs);
    }, [
        clearLiveArrivalTimer,
        finishRefreshWithoutArrival,
        liveArrivalItems,
        liveArrivalState,
    ]);
    const selectMode = (nextMode, direction) => {
        if (nextMode === mode) {
            setSwipeOffset(0);
            return;
        }
        if (mode === "feed" && nextMode !== "feed") {
            feedScrollTopRef.current =
                viewportRef.current?.scrollTop ?? 0;
        }
        const nextIndex = storyGameModes.indexOf(nextMode);
        const currentIndex = storyGameModes.indexOf(mode);
        setTabTransitionDirection(direction ??
            (nextIndex > currentIndex ? "left" : "right"));
        setSwipeOffset(0);
        if (activeMode === undefined) {
            setInternalMode(nextMode);
        }
        onModeChange?.(nextMode);
    };
    const selectAdjacentMode = (direction) => {
        selectMode(adjacentStoryGameMode(mode, direction, Boolean(stats)), direction);
    };
    const openArticleResolution = useMemo(() => openArticleId
        ? resolveArticleDestination(articles, {
            id: openArticleId,
            type: "article",
        })
        : null, [articles, openArticleId]);
    const handleHostIntent = (destination) => {
        if (destination.type !== "article") {
            return;
        }
        articleLauncherRef.current =
            document.activeElement instanceof HTMLElement
                ? document.activeElement
                : null;
        setOpenArticleId(destination.id);
    };
    const closeArticle = () => {
        setOpenArticleId(null);
        requestAnimationFrame(() => articleLauncherRef.current?.focus({ preventScroll: true }));
    };
    useBrowserLayoutEffect(() => {
        const viewport = viewportRef.current;
        if (!viewport) {
            return;
        }
        const playerHeight = playerRef.current?.offsetHeight ?? 0;
        const contextChanged = scrollContextKeyRef.current !== scrollContextKey;
        const target = gameModeScrollTarget({
            contextChanged,
            feedScrollTop: feedScrollTopRef.current,
            hasPersistentPlayer: viewingAccess === "league-pass",
            mode,
            playerHeight,
            previousMode: previousModeRef.current,
        });
        if (contextChanged) {
            feedScrollTopRef.current = 0;
        }
        if (target !== null) {
            viewport.scrollTo({ top: target });
        }
        previousModeRef.current = mode;
        scrollContextKeyRef.current = scrollContextKey;
    }, [mode, scrollContextKey, viewingAccess]);
    useEffect(() => {
        setVisibleFeedItemCount(12);
    }, [companion.gameId, companion.items]);
    useEffect(() => {
        clearLiveArrivalTimer();
        setLiveArrivalState("waiting");
        setIsRefreshing(false);
        setPullDistance(0);
    }, [
        clearLiveArrivalTimer,
        companion.gameId,
        liveArrivalItemIdsKey,
    ]);
    useEffect(() => {
        if (liveArrivalItems.length === 0 || liveArrivalState !== "waiting") {
            return;
        }
        const timeout = window.setTimeout(() => {
            const viewport = viewportRef.current;
            const isNearTop = mode === "feed" &&
                Boolean(viewport) &&
                viewport.scrollTop <= feedTop() + 56;
            if (isNearTop) {
                insertLiveArrival();
            }
            else {
                setLiveArrivalState("queued");
            }
        }, liveArrivalDelayMs);
        return () => window.clearTimeout(timeout);
    }, [
        feedTop,
        insertLiveArrival,
        liveArrivalItems,
        liveArrivalState,
        mode,
    ]);
    useEffect(() => () => {
        clearLiveArrivalTimer();
    }, [clearLiveArrivalTimer]);
    useEffect(() => {
        setIsStoryOpen(false);
        setIsAlertsOpen(false);
        setSelectedAlertIds(new Set());
        setHasNewStoryPages(companion.story?.state !== "seen");
    }, [companion.gameId, companion.story]);
    const handleViewportPointerDown = (event) => {
        if (event.button !== 0) {
            return;
        }
        const viewport = viewportRef.current;
        pointerStartRef.current = {
            canPull: mode === "feed" &&
                Boolean(viewport) &&
                viewport.scrollTop <= feedTop() + 2,
            x: event.clientX,
            y: event.clientY,
        };
    };
    const handleViewportPointerMove = (event) => {
        const start = pointerStartRef.current;
        if (!start) {
            return;
        }
        const deltaX = event.clientX - start.x;
        const deltaY = event.clientY - start.y;
        if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 8) {
            setPullDistance(0);
            setSwipeOffset(Math.max(-72, Math.min(72, deltaX)));
            return;
        }
        if (start.canPull && deltaY > 0) {
            setSwipeOffset(0);
            setPullDistance(Math.min(68, deltaY * 0.58));
        }
    };
    const finishViewportPointerGesture = (event) => {
        const start = pointerStartRef.current;
        pointerStartRef.current = null;
        if (start && event) {
            const deltaX = event.clientX - start.x;
            const deltaY = event.clientY - start.y;
            if (Math.abs(deltaX) > Math.abs(deltaY) &&
                Math.abs(deltaX) >= tabSwipeThreshold) {
                selectAdjacentMode(deltaX < 0 ? "left" : "right");
                setPullDistance(0);
                return;
            }
        }
        setSwipeOffset(0);
        if (pullDistance >= pullRefreshThreshold) {
            insertLiveArrival();
        }
        else {
            setPullDistance(0);
        }
    };
    const closeAlerts = () => {
        setIsAlertsOpen(false);
        requestAnimationFrame(() => alertButtonRef.current?.focus({ preventScroll: true }));
    };
    const toggleAlert = (id) => {
        setSelectedAlertIds((current) => {
            const next = new Set(current);
            if (next.has(id)) {
                next.delete(id);
            }
            else {
                next.add(id);
            }
            return next;
        });
    };
    const handleViewportScroll = () => {
        const viewport = viewportRef.current;
        if (mode !== "feed" ||
            !viewport ||
            !isNearFeedEnd(viewport) ||
            isLoadingOlderRef.current) {
            return;
        }
        isLoadingOlderRef.current = true;
        setVisibleFeedItemCount((current) => Math.min(current + 12, settledFeedItems.length));
        requestAnimationFrame(() => {
            isLoadingOlderRef.current = false;
        });
    };
    const tabs = (_jsxs("nav", { "aria-label": "Game screen sections", "aria-hidden": isOverlayOpen ? true : undefined, className: viewingAccess === "league-pass"
            ? "story-game__tabs story-game__tabs--sticky"
            : "story-game__tabs", inert: isOverlayOpen ? true : undefined, children: [storyGameModes.map((tab) => (_jsx("button", { "aria-current": mode === tab ? "page" : undefined, disabled: (tab === "box" || tab === "stats") && !stats, onClick: () => selectMode(tab), type: "button", children: tab === "box"
                    ? "Box Score"
                    : tab === "feed"
                        ? "Pulse"
                        : `${tab[0].toUpperCase()}${tab.slice(1)}` }, tab))), _jsx("span", { "aria-hidden": "true", className: "story-game__tab-indicator", style: {
                    transform: `translateX(${storyGameModes.indexOf(mode) * 100}%)`,
                }, children: _jsx("i", {}) })] }));
    const renderFeedItem = (item) => (_jsx("div", { children: _jsx(UnitCard, { isLiveGame: isInProgressGameScore(companion.score), item: item, onHostIntent: handleHostIntent, surface: "game", surfaceGameId: companion.gameId, onOpenVideo: (selectedItem, isMuted) => {
                clipLauncherRef.current =
                    document.activeElement instanceof HTMLElement
                        ? document.activeElement
                        : null;
                setOpenClip({
                    isMuted,
                    item: selectedItem,
                });
            } }) }, item.id));
    const liveUpdatePill = mode === "feed" && liveArrivalState === "queued" ? (_jsxs("button", { className: `story-game__update-pill ${viewingAccess === "league-pass"
            ? "story-game__update-pill--below-sticky-tabs"
            : ""}`, onClick: () => {
            scrollToFeedTop();
            insertLiveArrival();
        }, type: "button", children: [_jsxs("span", { children: [liveArrivalItems.length, " ", liveArrivalItems.length === 1 ? "update" : "updates"] }), _jsx(ArrowUp, { "aria-hidden": "true" })] })) : null;
    const modeContent = (_jsx("div", { className: "story-game__mode-stage", style: {
            "--story-game-swipe-offset": `${swipeOffset}px`,
        }, children: _jsxs("div", { className: `story-game__mode-panel ${tabTransitionDirection
                ? `story-game__mode-panel--from-${tabTransitionDirection}`
                : ""}`, onAnimationEnd: () => setTabTransitionDirection(null), children: [mode === "feed" ? (_jsxs("div", { "aria-label": `${companion.title} Pulse`, className: "story-game__feed", "data-live-arrival-item-ids": liveArrivalItemIdsKey, children: [_jsxs("div", { "aria-hidden": "true", className: `story-game__refresh ${isRefreshing
                                ? "story-game__refresh--active"
                                : ""}`, style: {
                                "--story-game-pull-distance": `${pullDistance}px`,
                            }, children: [_jsx(RefreshCw, {}), _jsx("span", { children: isRefreshing
                                        ? "Updating"
                                        : pullDistance >= pullRefreshThreshold
                                            ? "Release to update"
                                            : "Pull to refresh" })] }), liveArrivalState === "loading" ? (_jsxs("div", { "aria-label": "Loading latest game update", "aria-live": "polite", className: "story-game__arrival-skeleton", role: "status", children: [_jsx("span", {}), _jsx("span", {}), _jsx("span", {})] })) : null, displayedFeedItems.map(renderFeedItem), _jsx("div", { className: "feed-tail", children: hasOlderFeedItems ? (_jsx("span", { className: "visually-hidden", children: "Earlier coverage loads automatically" })) : (_jsx("span", { children: "Start of available coverage" })) })] })) : null, mode === "stats" && stats ? (_jsx(StatsPanel, { stats: stats })) : null, mode === "box" && stats ? (_jsx(BoxScorePanel, { stats: stats })) : null, mode === "watch" ? (_jsx(WatchPanel, { broadcastActionLabel: broadcastActionLabel, companion: companion, isFinal: isFinal, viewingAccess: viewingAccess })) : null] }, `${companion.gameId}:${mode}`) }));
    return (_jsxs("section", { "aria-label": "Story Feed game screen", className: embedded
            ? "story-game-phone story-game-phone--embedded"
            : "phone-shell story-game-phone", children: [_jsxs("div", { "aria-hidden": "true", className: "phone-shell__status story-game__device-status", inert: isOverlayOpen ? true : undefined, children: [_jsx("span", { children: deviceTime }), _jsx("span", { children: "\u25CF \u25CF \u25FC\uFE0E" })] }), _jsxs("header", { "aria-hidden": isOverlayOpen ? true : undefined, className: "story-game__header", inert: isOverlayOpen ? true : undefined, children: [_jsxs("div", { className: "story-game__scorebar", children: [onBack ? (_jsx("button", { "aria-label": "Back", onClick: onBack, type: "button", children: _jsx(ArrowLeft, { "aria-hidden": "true" }) })) : (_jsx("span", { "aria-hidden": "true", className: "story-game__back-placeholder" })), _jsxs("div", { className: "story-game__team-score story-game__team-score--away", children: [_jsx(TeamMark, { code: awayCode, label: game.away?.name ?? awayCode }), _jsx("strong", { children: parsed?.awayScore ?? "–" })] }), _jsxs("span", { className: "story-game__game-state", children: [parsed?.state ?? companion.score, parsed?.clock ? _jsx("small", { children: parsed.clock }) : null] }), _jsxs("div", { className: "story-game__team-score story-game__team-score--home", children: [_jsx("strong", { children: parsed?.homeScore ?? "–" }), _jsx(TeamMark, { code: homeCode, label: game.home?.name ?? homeCode })] }), _jsx("button", { "aria-expanded": isAlertsOpen, "aria-haspopup": "dialog", "aria-label": selectedAlertCount > 0
                                    ? `Manage game alerts, ${selectedAlertCount} selected`
                                    : "Set game alerts", className: `story-game__alert-button ${selectedAlertCount > 0
                                    ? "story-game__alert-button--active"
                                    : ""}`, onClick: () => setIsAlertsOpen(true), ref: alertButtonRef, type: "button", children: _jsx(Bell, { "aria-hidden": "true" }) })] }), viewingAccess === "league-pass" ? null : (_jsxs("button", { className: "story-game__watch-action", onClick: () => selectMode("watch"), type: "button", children: [_jsx(Tv, { "aria-hidden": "true" }), watchLabel] }))] }), viewingAccess === "league-pass" ? (_jsxs("div", { "aria-hidden": isOverlayOpen ? true : undefined, className: "story-game__viewport story-game__viewport--player-scroll", inert: isOverlayOpen ? true : undefined, onPointerCancel: () => {
                    pointerStartRef.current = null;
                    setPullDistance(0);
                    setSwipeOffset(0);
                }, onPointerDown: handleViewportPointerDown, onPointerMove: handleViewportPointerMove, onPointerUp: finishViewportPointerGesture, onScroll: handleViewportScroll, ref: viewportRef, role: "region", tabIndex: 0, children: [_jsxs("section", { "aria-label": "League Pass player", className: "story-game__persistent-player", ref: playerRef, children: [_jsx("div", { "aria-hidden": "true", children: isFinal ? _jsx(Play, {}) : _jsx(Radio, {}) }), _jsxs("span", { children: [_jsx("strong", { children: isFinal
                                            ? "Full game replay"
                                            : "Live on League Pass" }), _jsx("small", { children: isFinal
                                            ? "Watch from start"
                                            : "Tap for controls" })] })] }), tabs, liveUpdatePill, modeContent] })) : (_jsxs(_Fragment, { children: [tabs, _jsxs("div", { "aria-hidden": isOverlayOpen ? true : undefined, className: "story-game__viewport", inert: isOverlayOpen ? true : undefined, onPointerCancel: () => {
                            pointerStartRef.current = null;
                            setPullDistance(0);
                            setSwipeOffset(0);
                        }, onPointerDown: handleViewportPointerDown, onPointerMove: handleViewportPointerMove, onPointerUp: finishViewportPointerGesture, onScroll: handleViewportScroll, ref: viewportRef, role: "region", tabIndex: 0, children: [liveUpdatePill, modeContent] })] })), companion.story && !isOverlayOpen ? (_jsx("div", { className: "story-game__floating-story", children: _jsx(GameStoryMark, { awayCode: awayCode, hasNewPages: hasNewStoryPages, homeCode: homeCode, onOpen: () => {
                        setHasNewStoryPages(false);
                        setIsStoryOpen(true);
                    }, story: companion.story }) })) : null, _jsx("div", { "aria-hidden": "true", className: "story-game__home-indicator", children: _jsx("span", {}) }), openClip ? (_jsx(VerticalClipPlayer, { companion: companion, deviceTime: deviceTime, initialMuted: openClip.isMuted, item: openClip.item, onClose: () => {
                    setOpenClip(null);
                    requestAnimationFrame(() => clipLauncherRef.current?.focus({
                        preventScroll: true,
                    }));
                }, onGoToGame: () => {
                    setOpenClip(null);
                    requestAnimationFrame(() => clipLauncherRef.current?.focus({
                        preventScroll: true,
                    }));
                } })) : null, isStoryOpen && companion.story ? (_jsx(GameStoryPlayer, { companion: companion, item: storyItem, onClose: () => setIsStoryOpen(false), story: companion.story })) : null, isAlertsOpen ? (_jsx(GameAlertsSheet, { choices: alertChoices, onClose: closeAlerts, onToggle: toggleAlert, selectedIds: selectedAlertIds })) : null, openArticleId && openArticleResolution ? (_jsx(ArticleReader, { article: openArticleResolution.status === "available"
                    ? openArticleResolution.article
                    : null, articleId: openArticleId, onClose: closeArticle })) : null] }));
}
